(() => {
  if (window.LigeCheckout) return;
  const configNode = document.getElementById('lige-checkout-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const root = `${config.root.replace(/\/$/, '')}/`;
  const messages = config.messages;
  const providers = new Map();
  const pending = new Set();
  const purchases = new WeakMap();
  const trackingKeys = ['utm_source', 'utm_campaign', 'utm_term', 'utm_medium', 'utm_content', 'gclid', 'fbclid'];
  const previewParams = new URLSearchParams(location.search);
  const previewProvider = previewParams.get('lige_checkout_provider');
  const providerName = ['shopify', 'yampi', 'luna'].includes(previewProvider) ? previewProvider : config.provider;
  let inFlight = null;
  let navigating = false;
  let lastCartChange = 0;
  let cartError = '';
  let activeButton = null;

  function getCookie(name) {
    const prefix = `${name}=`;
    const cookie = document.cookie.split(';').map(part => part.trim()).find(part => part.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : '';
  }

  function rememberTracking() {
    try {
      const current = new URL(location.href);
      const stored = JSON.parse(sessionStorage.getItem('lige_checkout_tracking') || '{}');
      trackingKeys.forEach(key => {
        const value = current.searchParams.get(key);
        if (value) stored[key] = value;
      });
      sessionStorage.setItem('lige_checkout_tracking', JSON.stringify(stored));
    } catch (_) {}
  }

  rememberTracking();

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: { Accept: 'application/json', ...options.headers }
      });
      const data = await response.json();
      if (!response.ok || data.errors || Number(data.status) >= 400) {
        throw new Error(data.description || data.message || messages.error);
      }
      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  function post(url, data) {
    return request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  }

  function notify(error, button) {
    let target = document.getElementById('lige-checkout-message');
    if (!target) {
      target = document.createElement('div');
      target.id = 'lige-checkout-message';
      target.setAttribute('role', 'alert');
      target.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:100000;max-width:calc(100% - 32px);width:420px;padding:16px;background:#fff;color:#b42318;border:1px solid #b42318;border-radius:6px;box-shadow:0 4px 20px #0002;';
      document.body.append(target);
    }
    target.textContent = error.name === 'AbortError' || error instanceof TypeError ? messages.error : error.message || messages.error;
    target.hidden = false;
    button?.focus();
  }

  function setBusy(busy, button) {
    if (button) {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
    const loader = document.querySelector('.yampi-loader');
    if (loader) loader.style.display = busy && providerName === 'yampi' ? 'block' : 'none';
  }

  function checkTerms(scope) {
    const unchecked = scope?.querySelector('input[name="agree_terms"]:not(:checked)');
    if (unchecked) {
      unchecked.focus();
      throw new Error(messages.terms);
    }
  }

  async function waitForCart() {
    const wait = Math.max(0, 400 - (Date.now() - lastCartChange));
    if (wait) await new Promise(resolve => setTimeout(resolve, wait));
    const deadline = Date.now() + 20000;
    while (pending.size) {
      if (Date.now() > deadline) throw new Error(messages.cart_pending);
      await Promise.race([
        Promise.allSettled([...pending]),
        new Promise(resolve => setTimeout(resolve, 100))
      ]);
    }
    if (cartError) throw new Error(cartError);
  }

  function cartScope(button) {
    return button?.closest('m-cart-drawer, m-cart') || button?.form || null;
  }

  async function saveCartDetails(scope) {
    if (!scope) return;
    const update = {};
    const note = scope.querySelector('[name="note"]');
    if (note) update.note = note.value;
    scope.querySelectorAll('[name^="attributes["]').forEach(field => {
      const match = field.name.match(/^attributes\[(.+)\]$/);
      if (!match || field.disabled || /checkbox|radio/.test(field.type) && !field.checked) return;
      (update.attributes ||= {})[match[1]] = field.value;
    });
    if (Object.keys(update).length) await post(`${root}cart/update.js`, update);
  }

  function trackingUrl(value) {
    const target = new URL(value, location.href);
    if (target.protocol !== 'https:') throw new Error(messages.unavailable);
    try {
      if (providerName === 'yampi' && typeof window.ymp_getUrlWithUtms === 'function') {
        return window.ymp_getUrlWithUtms(target.href);
      }
      const stored = JSON.parse(sessionStorage.getItem('lige_checkout_tracking') || '{}');
      trackingKeys.forEach(key => {
        if (stored[key]) target.searchParams.set(key, stored[key]);
      });
      const ga = getCookie('_ga');
      if (ga) target.searchParams.set('_ga', ga);
    } catch (_) {}
    return target.href;
  }

  providers.set('shopify', async () => new URL(`${root}checkout`, location.origin).href);
  providers.set('yampi', async cart => {
    const result = await post('https://api.dooki.com.br/v2/public/shopify/cart', {
      shop: config.shop,
      shopify_internal_domain: config.shopifyDomain,
      cart_payload: cart
    });
    if (result.active === false || !result.checkout_direct_url) throw new Error(messages.unavailable);
    return trackingUrl(result.checkout_direct_url);
  });
  providers.set('luna', async cart => {
    const result = await post('https://api.lunacheckout.com/cart', {
      shop: config.shop,
      shopify_internal_domain: config.shopifyDomain,
      cart_payload: cart
    });
    if (result.active === false || !result.checkout_direct_url) throw new Error(messages.unavailable);
    return trackingUrl(result.checkout_direct_url);
  });

  async function redirect(cart) {
    if (!cart.item_count) throw new Error(messages.empty);
    const provider = providers.get(providerName);
    if (!provider) throw new Error(messages.unavailable);
    const target = new URL(await provider(cart));
    if (target.protocol !== 'https:') throw new Error(messages.unavailable);
    navigating = true;
    window.location.assign(target.href);
  }

  function perform(action, button) {
    if (inFlight || navigating) return inFlight || Promise.resolve();
    activeButton = button;
    const previousMessage = document.getElementById('lige-checkout-message');
    if (previousMessage) previousMessage.hidden = true;
    setBusy(true, button);
    inFlight = Promise.resolve().then(action).catch(error => {
      notify(error, button);
      throw error;
    }).finally(() => {
      if (!navigating) setBusy(false, button);
      inFlight = null;
    });
    return inFlight;
  }

  function checkout(button) {
    return perform(async () => {
      const scope = cartScope(button);
      checkTerms(scope);
      await waitForCart();
      await saveCartDetails(scope);
      await redirect(await request(`${root}cart.js`, { cache: 'no-store' }));
    }, button);
  }

  function purchaseData(button) {
    const form = button.form || button.closest('form');
    if (!form) throw new Error(messages.variant);
    const mainForm = document.querySelector('.m-product-form--main form');
    const sourceForm = form.closest('sticky-atc') && mainForm ? mainForm : form;
    const scope = sourceForm.closest('.m-main-product--info') || sourceForm;
    checkTerms(scope);
    const missing = typeof window.validateForm === 'function' ? window.validateForm(scope) : [];
    if (missing?.length || !sourceForm.reportValidity() || !form.reportValidity()) throw new Error(messages.required);
    const variant = form.querySelector('[name="id"]')?.value;
    const available = (button.dataset.ligeAvailableVariants || '').split(',');
    if (!variant || !available.includes(variant)) throw new Error(messages.variant);
    const field = form.querySelector('[name="quantity"]');
    const quantity = Number(field?.value || 1);
    if (!Number.isInteger(quantity) || quantity < 1 || field && !field.checkValidity()) throw new Error(messages.quantity);
    const body = new FormData(sourceForm);
    body.set('id', variant);
    body.set('quantity', String(quantity));
    ['form_type', 'utf8', 'return_to', 'checkout', 'add', 'sections', 'sections_url'].forEach(key => body.delete(key));
    const signature = JSON.stringify([...body].map(([key, value]) => [key, value instanceof File ? `${value.name}:${value.size}:${value.lastModified}` : value]));
    return { form, body, signature };
  }

  function buy(button) {
    return perform(async () => {
      const { form, body, signature } = purchaseData(button);
      await waitForCart();
      const previous = purchases.get(form);
      const cart = previous && previous.signature === signature ? await request(`${root}cart.js`, { cache: 'no-store' }) : null;
      const alreadyAdded = cart?.items.some(item => item.key === previous.key && item.quantity === previous.quantity);
      if (!alreadyAdded) {
        const added = await request(`${root}cart/add.js`, { method: 'POST', body });
        purchases.set(form, { signature, key: added.key, quantity: added.quantity });
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: added } }));
        document.dispatchEvent(new CustomEvent('notxs:cart-added'));
      }
      await redirect(await request(`${root}cart.js`, { cache: 'no-store' }));
      purchases.delete(form);
    }, button);
  }

  function isCheckoutLink(element) {
    if (element?.tagName !== 'A' || !element.href) return false;
    const url = new URL(element.href, location.href);
    return url.origin === location.origin && /\/(?:a\/)?checkout\/?$/.test(url.pathname);
  }

  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-lige-checkout], [name="checkout"], a[href]');
    if (!button || !button.hasAttribute('data-lige-checkout') && button.name !== 'checkout' && !isCheckoutLink(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled || button.closest('[data-terms-action][disabled]')) return;
    const action = button.dataset.ligeCheckout === 'buy' ? buy(button) : checkout(button);
    action.catch(() => {});
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    const button = event.submitter;
    if (!button || button.name !== 'checkout' && button.dataset.ligeCheckout !== 'cart') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(button).catch(() => {});
  }, true);

  document.addEventListener('change', event => {
    if (event.target.matches('[name="updates[]"], [name^="updates["]') && event.target.closest('m-cart, m-cart-drawer')) {
      lastCartChange = Date.now();
      cartError = '';
    }
  }, true);

  window.addEventListener('pageshow', () => {
    navigating = false;
    inFlight = null;
    setBusy(false, activeButton);
  });

  window.LigeCheckout = Object.freeze({
    provider: providerName,
    checkout,
    buy,
    register(name, handler) {
      if (typeof handler !== 'function') throw new TypeError('Checkout provider must be a function');
      providers.set(name, handler);
    },
    trackCartMutation(promise) {
      cartError = '';
      pending.add(promise);
      promise.then(() => pending.delete(promise), () => pending.delete(promise));
      return promise;
    },
    setCartError(message) { cartError = message; }
  });
})();
